import React, { useRef, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { blockRegistry } from "../registry/blocks";
import { uploadImage } from "../../utils/uploadImage";

const BRAND = "#069d6e";

function ImageField({ label, value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
    } catch (err) {
      setError(err.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <TextField
        size="small"
        label={label}
        value={value || ""}
        placeholder="Paste an image URL or upload"
        onChange={(e) => onChange(e.target.value)}
        fullWidth
      />
      {value ? (
        <img
          src={value}
          alt="preview"
          style={{
            maxWidth: "100%",
            maxHeight: 120,
            objectFit: "contain",
            borderRadius: 6,
            border: "1px solid #e2e8f0",
            background: "#f8fafc",
          }}
        />
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFile}
      />
      <Button
        size="small"
        variant="outlined"
        disabled={uploading}
        startIcon={
          uploading ? (
            <CircularProgress size={14} sx={{ color: BRAND }} />
          ) : (
            <CloudUploadIcon fontSize="small" />
          )
        }
        onClick={() => inputRef.current?.click()}
        sx={{
          borderColor: BRAND,
          color: BRAND,
          textTransform: "none",
          "&:hover": { borderColor: BRAND },
        }}
      >
        {uploading ? "Uploading..." : "Upload image"}
      </Button>
      {error ? (
        <span className="text-[11px] text-red-500">{error}</span>
      ) : null}
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value || "#000000"}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 38, height: 38, border: "none", background: "none" }}
      />
      <TextField
        size="small"
        label={label}
        value={value || ""}
        placeholder="default"
        onChange={(e) => onChange(e.target.value)}
        fullWidth
      />
    </div>
  );
}

function AlignField({ value, onChange }) {
  return (
    <div>
      <div className="text-xs text-gray-500 mb-1">Alignment</div>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={value || "left"}
        onChange={(_e, v) => v && onChange(v)}
      >
        <ToggleButton value="left">
          <FormatAlignLeftIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton value="center">
          <FormatAlignCenterIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton value="right">
          <FormatAlignRightIcon fontSize="small" />
        </ToggleButton>
      </ToggleButtonGroup>
    </div>
  );
}

function ItemsField({ field, value, onChange }) {
  const items = value || [];
  const update = (idx, key, val) => {
    const next = items.map((it, i) =>
      i === idx ? { ...it, [key]: val } : it
    );
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_it, i) => i !== idx));
  const add = () => onChange([...items, { ...field.newItem }]);

  return (
    <div>
      <div className="text-xs text-gray-500 mb-2">{field.label}</div>
      <div className="flex flex-col gap-3">
        {items.map((it, idx) => (
          <div
            key={idx}
            className="rounded-md border border-gray-200 p-2 flex flex-col gap-2"
          >
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-400">#{idx + 1}</span>
              <IconButton size="small" onClick={() => remove(idx)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </div>
            {field.itemFields.map((f) => (
              <TextField
                key={f.key}
                size="small"
                label={f.label}
                value={it[f.key] || ""}
                onChange={(e) => update(idx, f.key, e.target.value)}
                fullWidth
              />
            ))}
          </div>
        ))}
      </div>
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={add}
        sx={{ color: BRAND, mt: 1, textTransform: "none" }}
      >
        Add item
      </Button>
    </div>
  );
}

const BLANK_COLUMN = {
  icon: "",
  image: "",
  subtitle: "",
  subtitleColor: "#069d6e",
  title: "",
  text: "",
  linkLabel: "",
  linkHref: "",
  buttonLabel: "",
  buttonHref: "",
};

function ColumnsField({ value, onChange }) {
  const columns = value && value.length ? value : [{ ...BLANK_COLUMN }];

  const setCount = (count) => {
    if (count === columns.length) return;
    if (count < columns.length) {
      onChange(columns.slice(0, count));
    } else {
      const next = [...columns];
      while (next.length < count) next.push({ ...BLANK_COLUMN });
      onChange(next);
    }
  };

  const updateCol = (idx, key, val) =>
    onChange(columns.map((c, i) => (i === idx ? { ...c, [key]: val } : c)));

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="text-xs text-gray-500 mb-1">Number of columns</div>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={columns.length}
          onChange={(_e, v) => v && setCount(v)}
        >
          <ToggleButton value={2}>2 columns</ToggleButton>
          <ToggleButton value={3}>3 columns</ToggleButton>
        </ToggleButtonGroup>
      </div>
      {columns.map((col, idx) => (
        <div
          key={idx}
          className="rounded-md border border-gray-200 p-3 flex flex-col gap-2"
        >
          <div className="text-xs font-semibold text-gray-500">
            Column {idx + 1}
          </div>
          <ImageField
            label="Icon (optional)"
            value={col.icon}
            onChange={(v) => updateCol(idx, "icon", v)}
          />
          <ImageField
            label="Image"
            value={col.image}
            onChange={(v) => updateCol(idx, "image", v)}
          />
          <TextField
            size="small"
            label="Subtitle (optional)"
            value={col.subtitle || ""}
            onChange={(e) => updateCol(idx, "subtitle", e.target.value)}
            fullWidth
          />
          <ColorField
            label="Subtitle color"
            value={col.subtitleColor}
            onChange={(v) => updateCol(idx, "subtitleColor", v)}
          />
          <TextField
            size="small"
            label="Title"
            value={col.title || ""}
            onChange={(e) => updateCol(idx, "title", e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Text"
            value={col.text || ""}
            onChange={(e) => updateCol(idx, "text", e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            size="small"
            label="Link label (optional)"
            value={col.linkLabel || ""}
            onChange={(e) => updateCol(idx, "linkLabel", e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Link URL"
            value={col.linkHref || ""}
            onChange={(e) => updateCol(idx, "linkHref", e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Button label (optional)"
            value={col.buttonLabel || ""}
            onChange={(e) => updateCol(idx, "buttonLabel", e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Button URL"
            value={col.buttonHref || ""}
            onChange={(e) => updateCol(idx, "buttonHref", e.target.value)}
            fullWidth
          />
        </div>
      ))}
    </div>
  );
}

function Field({ field, value, onChange }) {
  switch (field.type) {
    case "label":
      return (
        <div className="mt-1 border-t border-gray-100 pt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          {field.label}
        </div>
      );
    case "textarea":
      return (
        <TextField
          size="small"
          label={field.label}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          multiline
          minRows={3}
        />
      );
    case "number":
      return (
        <TextField
          size="small"
          type="number"
          label={field.label}
          value={value ?? ""}
          onChange={(e) =>
            onChange(e.target.value === "" ? "" : Number(e.target.value))
          }
          fullWidth
        />
      );
    case "color":
      return <ColorField label={field.label} value={value} onChange={onChange} />;
    case "image":
      return <ImageField label={field.label} value={value} onChange={onChange} />;
    case "columns":
      return <ColumnsField field={field} value={value} onChange={onChange} />;
    case "select":
      return (
        <TextField
          select
          size="small"
          label={field.label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
        >
          {field.options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      );
    case "checkbox":
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              sx={{ "&.Mui-checked": { color: BRAND } }}
            />
          }
          label={field.label}
        />
      );
    case "align":
      return <AlignField value={value} onChange={onChange} />;
    case "items":
      return <ItemsField field={field} value={value} onChange={onChange} />;
    case "text":
    default:
      return (
        <TextField
          size="small"
          label={field.label}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
        />
      );
  }
}

function GlobalSettings({ settings, onChange }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="text-[11px] text-gray-400">
        Select a block to edit it, or adjust the overall email below.
      </div>
      <TextField
        size="small"
        type="number"
        label="Content width (px)"
        value={settings.contentWidth}
        onChange={(e) => onChange({ contentWidth: Number(e.target.value) })}
        fullWidth
      />
      <ColorField
        label="Page background"
        value={settings.backgroundColor}
        onChange={(v) => onChange({ backgroundColor: v })}
      />
      <ColorField
        label="Card background"
        value={settings.cardColor}
        onChange={(v) => onChange({ cardColor: v })}
      />
      <ColorField
        label="Default text color"
        value={settings.textColor}
        onChange={(v) => onChange({ textColor: v })}
      />
      <ColorField
        label="Link color"
        value={settings.linkColor}
        onChange={(v) => onChange({ linkColor: v })}
      />
      <TextField
        size="small"
        label="Font family"
        value={settings.fontFamily}
        onChange={(e) => onChange({ fontFamily: e.target.value })}
        fullWidth
      />
    </div>
  );
}

export default function Inspector({
  block,
  settings,
  onUpdateBlock,
  onUpdateSettings,
}) {
  if (!block) {
    return (
      <div className="h-full flex flex-col">
        <div className="sticky top-0 z-10 border-b border-gray-100 bg-white px-4 py-3 text-sm font-semibold text-gray-800">
          Email settings
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <GlobalSettings settings={settings} onChange={onUpdateSettings} />
        </div>
      </div>
    );
  }

  const def = blockRegistry[block.type];
  const Icon = def.icon;
  return (
    <div className="h-full flex flex-col">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-gray-100 bg-white px-4 py-3">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-md"
          style={{ backgroundColor: `${BRAND}14` }}
        >
          <Icon style={{ color: BRAND }} fontSize="small" />
        </span>
        <span className="text-sm font-semibold text-gray-800">
          {def.label}
        </span>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-4">
          {def.fields.map((field) => (
            <Field
              key={field.key}
              field={field}
              value={block.props[field.key]}
              onChange={(val) => onUpdateBlock(block.id, { [field.key]: val })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
